// Companion/blocks/FlashcardsBlock.tsx
import { FlashcardsBlockBody } from './bodies/FlashcardsBlockBody/FlashcardsBlockBody';
import type { ChatFlashcardsBlockPayload } from '@/types/chatBlocks';
import {
  CHAT_ANCHOR_LABEL_DETAIL_MAX_CHARS,
  toChatAnchorLabel,
} from '@/chatContext/chatAnchorLabels';
import { useBlockAnchor } from './useBlockAnchor';

/**
 * Flashcards served into the thread: the ported `FlashcardsBlockBody`, which
 * owns the flip, the deck position, and the keyboard handling.
 *
 * The anchor it reports back only ever says which card is on screen and whether
 * the back is showing; the "revealed" half of that is what stops the companion
 * spoiling a card the student has not flipped (spec, "Answer visibility").
 */
interface FlashcardsBlockProps {
  payload: ChatFlashcardsBlockPayload;
  isReadOnly: boolean;
}

const FlashcardsBlock = ({ payload, isReadOnly }: FlashcardsBlockProps) => {
  const { claim } = useBlockAnchor(isReadOnly);

  const items = payload.cards.map((card) => ({
    id: card.id,
    front: card.front,
    back: card.back,
    explanation_short: card.explanation ?? '',
  }));

  return (
    <>
      <FlashcardsBlockBody
        items={items}
        onAnchorChange={(anchor) => {
          const flashcardId =
            typeof anchor?.id === 'string' ? anchor.id : payload.cards[0]?.id;

          if (!flashcardId) return;

          const flashcard = (anchor?.flashcard ?? {}) as Record<
            string,
            unknown
          >;

          claim({
            type: 'flashcard',
            flashcardId,
            revealed: flashcard.face === 'back',
            // The pill names the card the same way the study surface does
            // (ORA-662), from the front the block was built with.
            label: toChatAnchorLabel(
              'Flashcard',
              payload.cards.find((card) => card.id === flashcardId)?.front,
              CHAT_ANCHOR_LABEL_DETAIL_MAX_CHARS
            ),
          });
        }}
      />
    </>
  );
};

export default FlashcardsBlock;
